import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import type { MeetingLabel, MeetingTopicSummary } from '../lib/types';

export function MeetingTaxonomySettings() {
  const client = useQueryClient();
  const topics = useQuery({
    queryKey: ['meeting-topics', 'settings'],
    queryFn: () =>
      api<{ data: MeetingTopicSummary[] }>('/api/meeting-topics?status=active&limit=100').then(
        (r) => r.data,
      ),
  });
  const labels = useQuery({
    queryKey: ['meeting-labels'],
    queryFn: () => api<{ data: MeetingLabel[] }>('/api/meeting-labels').then((r) => r.data),
  });
  const [topicName, setTopicName] = useState('');
  const [labelName, setLabelName] = useState('');
  const createTopic = useMutation({
    mutationFn: () =>
      api('/api/meeting-topics', {
        method: 'POST',
        body: JSON.stringify({ title: topicName.trim(), color: '#0f766e' }),
      }),
    onSuccess: async () => {
      setTopicName('');
      await client.invalidateQueries({ queryKey: ['meeting-topics'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    },
  });
  const createLabel = useMutation({
    mutationFn: () =>
      api('/api/meeting-labels', {
        method: 'POST',
        body: JSON.stringify({ name: labelName.trim(), color: '#0f766e' }),
      }),
    onSuccess: async () => {
      setLabelName('');
      await client.invalidateQueries({ queryKey: ['meeting-labels'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    },
  });
  const editTopic = async (topic: MeetingTopicSummary) => {
    const name = window.prompt('ชื่อหมวดใหม่', topic.title);
    if (name?.trim() && name.trim() !== topic.title) {
      await api(`/api/meeting-topics/${topic.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: name.trim() }),
      });
      await client.invalidateQueries({ queryKey: ['meeting-topics'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    }
  };
  const removeTopic = async (topic: MeetingTopicSummary) => {
    const targetName =
      topic.meetingCount > 0
        ? window.prompt(`หมวดนี้มี ${topic.meetingCount} รายการ กรุณาพิมพ์ชื่อหมวดปลายทาง`)
        : null;
    if (topic.meetingCount > 0 && !targetName) return;
    const target = targetName
      ? (topics.data ?? []).find(
          (item) => item.title.trim() === targetName.trim() && item.id !== topic.id,
        )
      : undefined;
    if (topic.meetingCount > 0 && !target) {
      window.alert('ไม่พบหมวดปลายทาง');
      return;
    }
    if (window.confirm(`ยืนยันลบหมวด “${topic.title}” หรือไม่?`)) {
      await api(
        `/api/meeting-topics/${topic.id}${target ? `?replacementTopicId=${encodeURIComponent(target.id)}` : ''}`,
        { method: 'DELETE' },
      );
      await client.invalidateQueries({ queryKey: ['meeting-topics'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    }
  };
  const editLabel = async (label: MeetingLabel) => {
    const name = window.prompt('ชื่อ Label ใหม่', label.name);
    if (!name?.trim()) return;
    const color = window.prompt('สี Label (เช่น #0f766e)', label.color);
    if (color && !/^#[0-9a-f]{6}$/i.test(color.trim())) {
      window.alert('รูปแบบสีไม่ถูกต้อง');
      return;
    }
    if (name.trim() !== label.name || (color && color.trim() !== label.color)) {
      await api(`/api/meeting-labels/${label.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), ...(color ? { color: color.trim() } : {}) }),
      });
      await client.invalidateQueries({ queryKey: ['meeting-labels'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    }
  };
  const removeLabel = async (label: MeetingLabel) => {
    if (window.confirm(`ยืนยันลบ Label “${label.name}” หรือไม่?`)) {
      await api(`/api/meeting-labels/${label.id}`, { method: 'DELETE' });
      await client.invalidateQueries({ queryKey: ['meeting-labels'] });
      await client.invalidateQueries({ queryKey: ['color-groups'] });
    }
  };
  return (
    <div className="space-y-4">
      <section className="card">
        <h2 className="font-semibold">หมวดหมู่การประชุม</h2>
        <p className="mt-1 text-sm text-slate-500">สร้าง แก้ไข หรือลบหมวดสำหรับจัดกลุ่ม Tree</p>
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (topicName.trim()) createTopic.mutate();
          }}
        >
          <input
            className="field"
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
            placeholder="ชื่อหมวดใหม่"
          />
          <button className="btn-primary shrink-0" type="submit" disabled={!topicName.trim()}>
            สร้างหมวด
          </button>
        </form>
        <div className="mt-3 space-y-2">
          {(topics.data ?? [])
            .filter((topic) => !topic.systemKey)
            .map((topic) => (
              <div
                key={topic.id}
                className="flex items-center justify-between rounded-xl border p-3"
              >
                <span>{topic.title}</span>
                <span className="flex gap-3 text-sm">
                  <button
                    className="font-semibold text-teal-700"
                    onClick={() => void editTopic(topic)}
                  >
                    แก้ไข
                  </button>
                  <button
                    className="font-semibold text-red-600"
                    onClick={() => void removeTopic(topic)}
                  >
                    ลบ
                  </button>
                </span>
              </div>
            ))}
        </div>
      </section>
      <section className="card">
        <h2 className="font-semibold">Label การประชุม</h2>
        <p className="mt-1 text-sm text-slate-500">ตั้งชื่อและสีของป้ายสำหรับกรองรายการ</p>
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (labelName.trim()) createLabel.mutate();
          }}
        >
          <input
            className="field"
            value={labelName}
            onChange={(e) => setLabelName(e.target.value)}
            placeholder="ชื่อ Label ใหม่"
          />
          <button className="btn-primary shrink-0" type="submit" disabled={!labelName.trim()}>
            สร้าง Label
          </button>
        </form>
        <div className="mt-3 space-y-2">
          {(labels.data ?? []).map((label) => (
            <div key={label.id} className="flex items-center justify-between rounded-xl border p-3">
              <span className="flex items-center gap-2">
                <i className="h-3 w-3 rounded-full" style={{ backgroundColor: label.color }} />
                {label.name}
              </span>
              <span className="flex gap-3 text-sm">
                <button
                  className="font-semibold text-teal-700"
                  onClick={() => void editLabel(label)}
                >
                  แก้ไข
                </button>
                <button
                  className="font-semibold text-red-600"
                  onClick={() => void removeLabel(label)}
                >
                  ลบ
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
